import { apiClient } from './client';

export interface Goal {
  id: string;
  employeeId: string;
  title: string;
  target: number;
  current: number;
  startDate: string;
  endDate: string;
  deadline?: string | null;
  reviewDate?: string | null;
  status: 'on-track' | 'at-risk' | 'completed';
  notes?: string | null;
  reviewFeedback?: string | null;
  changeHistory: GoalChange[];
  createdAt: string;
  updatedAt: string;
}

export interface GoalChange {
  field: string;
  oldValue: string | number | null;
  newValue: string | number | null;
  changedBy?: string | null;
  changedByName?: string | null;
  changedAt: string;
}

export interface CreateGoalRequest {
  title: string;
  target: number;
  current?: number;
  startDate: string;
  endDate: string;
  deadline?: string;
  reviewDate?: string;
  status?: 'on-track' | 'at-risk' | 'completed';
  notes?: string;
}

export interface UpdateGoalRequest {
  title?: string;
  target?: number;
  current?: number;
  startDate?: string;
  endDate?: string;
  deadline?: string;
  reviewDate?: string;
  status?: 'on-track' | 'at-risk' | 'completed';
  notes?: string;
  reviewFeedback?: string;
}

export const goalsApi = {
  /**
   * Get goals for a specific employee
   */
  getEmployeeGoals: async (employeeId: string): Promise<Goal[]> => {
    const response = await apiClient.get<Goal[]>(
      `/employees/${employeeId}/goals`
    );
    return response.data;
  },

  /**
   * Get single goal by ID
   */
  getGoal: async (goalId: string): Promise<Goal> => {
    const response = await apiClient.get<Goal>(`/goals/${goalId}`);
    return response.data;
  },

  /**
   * Create new goal for an employee
   */
  createGoal: async (
    employeeId: string,
    data: CreateGoalRequest
  ): Promise<Goal> => {
    const response = await apiClient.post<Goal>(
      `/employees/${employeeId}/goals`,
      data
    );
    return response.data;
  },

  /**
   * Update existing goal
   */
  updateGoal: async (goalId: string, data: UpdateGoalRequest): Promise<Goal> => {
    const response = await apiClient.put<Goal>(
      `/goals/${goalId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete goal
   */
  deleteGoal: async (goalId: string): Promise<void> => {
    await apiClient.delete(`/goals/${goalId}`);
  },

  /**
   * Get goal change history
   */
  getGoalHistory: async (goalId: string): Promise<GoalChange[]> => {
    const response = await apiClient.get<GoalChange[]>(
      `/goals/${goalId}/history`
    );
    return response.data;
  },
};
